import type { ColumnsType, AnyObject } from "@similarweb/pro-logic-components";
import { PixelPlaceholderLoader } from "@similarweb/ui-components";

import { NO_DATA_SIGN } from "app/filters/const";
import { translate } from "app/filters/i18nFilter";

import { SearchOverviewTexts } from "../../texts";
import type { FormattedNumber } from "../../types";
import { ESourceType } from "../../types";
import { renderChangeCell, renderDomainCell, renderProgressBarCell } from "../TableCells/tableCells";

import { CellWithLeftPadding, CellWithRightPadding, LastSubColumn } from "./styledComponents";
import type { OrganicPaidData } from "./types";

const renderWithLoading = (isLoading: boolean, content: React.ReactNode) => {
    return isLoading ? (
        <PixelPlaceholderLoader
            width="100%"
            height={6}
        />
    ) : (
        content
    );
};

const getSubColumns = ({
    type,
    lastSubColRightPadding = true,
    isLoading,
}: {
    type: ESourceType.ORGANIC | ESourceType.PAID;
    isLoading: boolean;
    lastSubColRightPadding?: boolean;
}): ColumnsType<AnyObject> => [
    {
        title: translate("analysis.source.search.overview.visits"),
        dataIndex: type,
        align: "right",
        width: 77,
        render: (data: OrganicPaidData) => data?.visits ?? NO_DATA_SIGN,
    },
    {
        title: translate(SearchOverviewTexts.yoyChange),
        dataIndex: type,
        width: 86,
        align: "right",
        render: (data: OrganicPaidData) => renderChangeCell(data?.change),
    },
    {
        title: translate(SearchOverviewTexts.keywords),
        dataIndex: type,
        align: "right",
        width: 77,
        render: (data: OrganicPaidData) => renderWithLoading(isLoading, data?.numOfKeywords ?? NO_DATA_SIGN),
    },
    {
        title: (
            <CellWithRightPadding withRightPadding={lastSubColRightPadding}>
                {translate(SearchOverviewTexts.yoyChange)}
            </CellWithRightPadding>
        ),
        dataIndex: type,
        align: "right",
        width: 96,
        render: (data: OrganicPaidData) =>
            renderWithLoading(
                isLoading,
                <LastSubColumn withRightPadding={lastSubColRightPadding}>
                    {renderChangeCell(data?.changeOfKeywords)}
                </LastSubColumn>,
            ),
    },
];

export const getOrganicPaidTrafficColumns = ({
    isLoadingOrganicKeywords,
    isLoadingPaidKeywords,
}: {
    isLoadingOrganicKeywords: boolean;
    isLoadingPaidKeywords: boolean;
}): ColumnsType<AnyObject> => [
    {
        title: <CellWithLeftPadding>{translate("analysis.source.search.overview.domain")}</CellWithLeftPadding>,
        dataIndex: "domain",
        width: 172,
        render: renderDomainCell,
    },
    {
        title: translate(SearchOverviewTexts.organicVsPaid),
        width: 156,
        dataIndex: "percentageOf",
        render: (percentageOf: { organic?: FormattedNumber; paid?: FormattedNumber }) => {
            const organic = percentageOf?.organic;
            const paid = percentageOf?.paid;
            return organic && paid
                ? renderProgressBarCell({
                      leftValue: organic.formattedValue,
                      rightValue: paid.formattedValue,
                      percentageValue: organic.numericValue,
                  })
                : NO_DATA_SIGN;
        },
    },
    {
        title: translate("analysis.source.search.overview.organic"),
        dataIndex: "organic",
        children: getSubColumns({
            type: ESourceType.ORGANIC,
            lastSubColRightPadding: false,
            isLoading: isLoadingOrganicKeywords,
        }),
    },
    {
        title: translate("analysis.source.search.overview.paid"),
        dataIndex: "paid",
        children: getSubColumns({
            type: ESourceType.PAID,
            isLoading: isLoadingPaidKeywords,
        }),
    },
];
